import { useCallback, useState } from 'react';
import { useIonToast } from '@ionic/react';
import { Subscription } from 'rxjs';

import { PlayerService } from 'services/player';
import {
  isPlayerScopedLoaded,
  playerScopedKey,
  selectedEgmsListBucket,
  sharedPlayerScopedGet,
} from 'lib/playerScopedListCache';

/**
 * Backend persistence for the casino-scoped "Selected EGMs" working set. Unlike
 * playlists/favorites this has no dedicated Redux slice — the live set stays in
 * `compareSlotsState.selectedSlots`. This hook only handles the HTTP load/save:
 *
 *  - `fetchUserSelectedEgms` loads a casino's saved selection and hands it back
 *    via `onLoaded` (the caller dispatches it into the compare state and tracks
 *    its sync refs). On failure it reports `[]` so the caller's refs still settle
 *    without clobbering whatever the user does next.
 *  - `persistSelectedEgms` replaces the whole stored array in one call.
 *
 * GetUserSelectedEgms is timeframe-independent: once per appUserPk+casinoId
 * with in-flight dedupe (same pattern as favorites/playlist).
 */
const useSelectedEgms = () => {
  const playerSvc = new PlayerService();
  const [presentToast] = useIonToast();
  const [isSelectedEgmsLoading, setIsSelectedEgmsLoading] = useState(false);

  const fetchUserSelectedEgms = useCallback(
    (userId: string, casinoId: string, onLoaded: (slotNumbers: number[]) => void): Subscription | undefined => {
      if (!userId || !casinoId) return undefined;
      const key = playerScopedKey(userId, casinoId);
      const alreadyLoaded = isPlayerScopedLoaded(selectedEgmsListBucket, key);
      if (!alreadyLoaded) setIsSelectedEgmsLoading(true);
      return sharedPlayerScopedGet(selectedEgmsListBucket, key, () =>
        playerSvc.GetUserSelectedEgms(userId, casinoId)
      ).subscribe({
        next(response) {
          setIsSelectedEgmsLoading(false);
          const slotNumbers = (response?.DataSet ?? [])
            .map((item: { slotNumber?: number | string }) => Number(item.slotNumber))
            .filter((slotNumber: number) => !Number.isNaN(slotNumber));
          onLoaded(slotNumbers);
        },
        error() {
          setIsSelectedEgmsLoading(false);
          onLoaded([]);
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Failed to get selected EGMs.',
          }).catch(() => null);
        },
      });
    },
    []
  );

  const persistSelectedEgms = useCallback(
    (slotNumbers: number[], userId: string, casinoId: string): Subscription | undefined => {
      if (!userId || !casinoId) return undefined;
      return playerSvc.SetUserSelectedEgms(slotNumbers.join(','), userId, casinoId).subscribe({
        error() {
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Failed to save selected EGMs.',
          }).catch(() => null);
        },
      });
    },
    []
  );

  return { isSelectedEgmsLoading, fetchUserSelectedEgms, persistSelectedEgms };
};

export default useSelectedEgms;
